import React from 'react';

import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { CurrencyChip, CurrencyIcon, FieldText, type FieldTextProps } from '@pkg/components';
import { type BaseCurrencyType } from '@pkg/types';
import { htmlPattern, roundToCommonDecimals } from '@pkg/utils';

import { LocalCurrencySelector, type LocalCurrencySelectorProps } from '../LocalCurrencySelector';

import { FormGroupControl, InputPrefix, InputSuffix, StyledEditablePrice } from './EditablePrice.style';
import { tokens } from './EditablePrice.tokens';

export interface EditablePriceProps {
    localCurrency?: BaseCurrencyType;
    nativeCurrency?: BaseCurrencyType;
    localPrice?: FieldTextProps['value'];
    nativePrice?: number;
    appraisal?: number;
    disabled?: boolean;
    error?: boolean;
    onPriceBlur?: FieldTextProps['onBlur'];
    onPriceChange?: FieldTextProps['onChange'];
    onLocalCurrencyChange?: LocalCurrencySelectorProps['onSelect'];
}

function safeNativeValue(price = 0) {
    if (price < 0.1) return '<0.1';
    return price > 999 ? '>999' : price;
}

export function EditablePrice({
    localCurrency = 'usd',
    nativeCurrency = 'usd',
    localPrice,
    nativePrice = 0,
    appraisal = 0,
    disabled = false,
    error = false,
    onPriceBlur,
    onPriceChange,
    onLocalCurrencyChange,
}: EditablePriceProps) {
    const [translate] = useTranslation('marketplace');

    const equalCurrencyTypes = localCurrency === nativeCurrency;
    const placeholderAppraisal = appraisal ? appraisal.toString() : '0.0';

    const currencyPrefix = (
        <InputPrefix>
            <CurrencyIcon currency={localCurrency} theme="glyph" />
        </InputPrefix>
    );

    const nativeSuffix =
        equalCurrencyTypes || nativePrice <= 0 ? null : (
            <InputSuffix key="NativeSuffix">
                <CurrencyChip
                    currency={nativeCurrency}
                    value={safeNativeValue(roundToCommonDecimals(nativePrice))}
                    maxLength={tokens.suffix.maxLength}
                />
            </InputSuffix>
        );

    return (
        <StyledEditablePrice>
            <FormGroupControl>
                <LocalCurrencySelector
                    ariaLabel={translate('asset-listing-sheet-local-currency-selector-cta-a11y')}
                    disabled={disabled || !onLocalCurrencyChange}
                    selected={localCurrency}
                    onSelect={onLocalCurrencyChange}
                />
            </FormGroupControl>

            <FormGroupControl>
                <FieldText
                    prefixPassthrough
                    size="small"
                    inputMode="decimal"
                    pattern={htmlPattern.float}
                    value={localPrice}
                    prefix={currencyPrefix}
                    prefixOffset={tokens.prefix.offset}
                    suffix={<AnimatePresence>{nativeSuffix}</AnimatePresence>}
                    suffixOffset={nativeSuffix ? tokens.suffix.offset : undefined}
                    placeholder={placeholderAppraisal}
                    disabled={disabled || !onPriceChange}
                    error={error}
                    onBlur={onPriceBlur}
                    onChange={onPriceChange}
                />
            </FormGroupControl>
        </StyledEditablePrice>
    );
}
